import { BaseAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import AllQuestionsForm from "../../AssignmentGenerator/components/forms/AllQuestionsForm";
import { EditAssignmentRequestBodyEdited } from "@packages/api/madlenAPI.schemas";
import {
  getGetAssignmentFromTeacherQueryKey,
  getGetAssignmentStudentsQueryKey,
  useEditAssignment,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import RegenModal from "../../AssignmentGenerator/components/forms/RegenModal";

const SingleAssignmentEditTab = ({
  assignment,
}: {
  assignment: BaseAssignmentResponseDto;
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [previousQuestionsCount, setPreviousQuestionsCount] = useState(
    assignment.questions.length,
  );
  const [floatingNotifications, setFloatingNotifications] = useState<
    { id: number; top: number; left: number }[]
  >([]);
  const [searchParams] = useSearchParams();
  const [regenModalOpen, setRegenModalOpen] = useState<number | null>(null);
  const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const currentCount = assignment.questions.length;
    if (currentCount > previousQuestionsCount) {
      const newNotification = {
        id: Date.now(),
        top: Math.random() * 60 + 20,
        left: Math.random() * 60 + 20,
      };
      setFloatingNotifications((prev) => [...prev, newNotification]);

      setTimeout(() => {
        setFloatingNotifications((prev) =>
          prev.filter((n) => n.id !== newNotification.id),
        );
      }, 1000);
    }
    setPreviousQuestionsCount(currentCount);
  }, [assignment.questions.length, previousQuestionsCount]);

  // Scroll to question if questionId is in URL
  useEffect(() => {
    const questionId = searchParams.get("questionId");
    if (questionId) {
      setTimeout(() => {
        questionRefs.current[questionId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [searchParams]);

  const getAssignmentFromTeacherQueryKey = getGetAssignmentFromTeacherQueryKey(
    Number(assignment.id),
  );

  const queryClient = useQueryClient();

  const { mutate: updateAssignment } = useEditAssignment({
    mutation: {
      onMutate: (variables) => {
        const previousData =
          queryClient.getQueryData<BaseAssignmentResponseDto>(
            getAssignmentFromTeacherQueryKey,
          );
        queryClient.setQueryData(
          getAssignmentFromTeacherQueryKey,
          (old: BaseAssignmentResponseDto | undefined) => {
            return (
              old && {
                ...old,
                questions: old.questions
                  .filter(
                    (question) =>
                      !variables.data?.deletedIds?.includes(question.id),
                  )
                  .map((question) => {
                    if (variables.data?.edited?.[question.id]) {
                      return variables.data?.edited?.[question.id];
                    }
                    return question;
                  }),
              }
            );
          },
        );
        return { previousData };
      },
      onError: (_error, _variables, context) => {
        queryClient.setQueryData(
          getAssignmentFromTeacherQueryKey,
          context?.previousData,
        );
        addToast({
          title: t("notifications.assignment.updateError"),
          description: t("notifications.assignment.updateErrorDescription"),
          variant: "error",
          duration: 3000,
        });
      },
      onSuccess: () => {
        addToast({
          title: t("notifications.assignment.updateSuccess"),
          variant: "success",
          duration: 1000,
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getAssignmentFromTeacherQueryKey,
        });
        queryClient.invalidateQueries({
          queryKey: getGetAssignmentStudentsQueryKey(Number(assignment.id)),
        });
      },
    },
  });

  return (
    <>
      {regenModalOpen !== null && (
        <RegenModal
          show={regenModalOpen !== null}
          onCloseHandler={() => setRegenModalOpen(null)}
          question={
            assignment.questions.find(
              (question) => question.id === regenModalOpen,
            )!
          }
          resetToOriginalQuestion={(questionId: number) => {
            const originalQuestion = localStorage.getItem(
              `regen-${questionId}`,
            );
            if (originalQuestion) {
              const parsedQuestion = JSON.parse(originalQuestion);
              updateAssignment({
                assignmentId: assignment.id,
                data: {
                  edited: {
                    [questionId]: parsedQuestion,
                  },
                  deletedIds: [],
                  newlyCreated: [],
                },
              });
            }
          }}
        />
      )}
      <AllQuestionsForm
        questions={assignment.questions}
        canEdit={assignment.status === "DRAFT"}
        questionsLoading={assignment.status === "PENDING"}
        questionRef={questionRefs}
        updateAssignment={updateAssignment}
        gradeLevel={assignment.grade}
        onRegenerateQuestion={(questionId) => {
          setRegenModalOpen(questionId);
        }}
        onQuestionsChange={(questions) => {
          const changedQuestions = questions.filter((q, i) => {
            const originalQuestion = assignment.questions[i];
            if (!originalQuestion) return true; // New question

            return JSON.stringify(q) !== JSON.stringify(originalQuestion);
          });

          if (changedQuestions.length > 0) {
            updateAssignment({
              assignmentId: assignment.id,
              data: {
                deletedIds: [],
                newlyCreated: [],
                edited: changedQuestions.reduce(
                  (acc, question) => {
                    acc[question.id] =
                      question as EditAssignmentRequestBodyEdited[keyof EditAssignmentRequestBodyEdited];
                    return acc;
                  },
                  {} as Record<
                    string,
                    EditAssignmentRequestBodyEdited[keyof EditAssignmentRequestBodyEdited]
                  >,
                ),
              },
            });
          }
        }}
      />

      {assignment.status === "PENDING" && (
        <div
          className="flex flex-col items-center justify-center py-12"
          data-testid="assignment-generating-loader"
        >
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 border-4 border-primary-100 dark:border-primary-900 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary-500 rounded-full animate-[spin_3s_linear_infinite]"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center">
                <div className="flex space-x-1">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
            {floatingNotifications.map((notification) => (
              <div
                key={notification.id}
                className="absolute text-indigo-500 font-bold text-3xl animate-float-away pointer-events-none"
                style={{
                  top: `${notification.top}%`,
                  left: `${notification.left}%`,
                }}
              >
                +1
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default SingleAssignmentEditTab;
